/**
 * InviteMemberDialog Component
 *
 * Dialog for inviting users to a workspace with email and role selection
 */

import { useState } from 'react';
import { Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@loader/ui';
import { Button } from '@loader/ui';
import { Input } from '@loader/ui';
import { Label } from '@loader/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import type { WorkspaceRole } from '@loader/shared-types';

import { FoldingCube } from '@/components/common';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';

/**
 * Props for InviteMemberDialog component
 */
interface InviteMemberDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Workspace ID to invite user to */
  workspaceId: string;
  /** Callback when invitation is successful */
  onInviteSuccess?: () => void;
}

/**
 * Role options for workspace invitations
 */
const ROLE_OPTIONS: Array<{ value: WorkspaceRole; label: string; description: string }> = [
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Can view workspace content',
  },
  {
    value: 'editor',
    label: 'Editor',
    description: 'Can view and edit workspace content',
  },
  {
    value: 'owner',
    label: 'Owner',
    description: 'Full access including member management',
  },
];

/**
 * Validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * InviteMemberDialog Component
 *
 * Provides a form to invite users to a workspace by email with role selection
 */
export function InviteMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  onInviteSuccess,
}: InviteMemberDialogProps) {
  const rpc = useRpc();
  const { error: showError, success: showSuccess } = useToastStore();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  /**
   * Validate form inputs
   */
  const isFormValid = (): boolean => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return false;
    }

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError(null);
    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (!isFormValid()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setEmailError(null);

      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].invitations.$post({
        param: { id: workspaceId },
        json: {
          email: email.trim().toLowerCase(),
          role,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        showError(
          'Failed to invite user',
          (errorData as { message?: string }).message || 'Could not send invitation'
        );
        return;
      }

      const data = await res.json();

      showSuccess(
        'Invitation sent',
        data.invitation.isNewUser
          ? `Invitation sent to ${email}. They will receive an email to join.`
          : `${email} has been added to the workspace.`
      );

      // Reset form
      setEmail('');
      setRole('viewer');
      setEmailError(null);

      // Close dialog
      onOpenChange(false);

      // Notify parent of success
      if (onInviteSuccess) {
        onInviteSuccess();
      }
    } catch (err) {
      console.error('Failed to invite user:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send invitation';
      showError('Failed to invite user', errorMessage);
      setEmailError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setRole('viewer');
      setEmailError(null);
      onOpenChange(false);
    }
  };

  /**
   * Handle email input change
   */
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join this workspace. The user will receive an
            email with instructions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={() => {
                  if (email && !isValidEmail(email)) {
                    setEmailError('Please enter a valid email address');
                  }
                }}
                className={emailError ? 'border-destructive pr-10 pl-10' : 'pl-10'}
                disabled={isSubmitting}
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'email-error' : undefined}
              />
            </div>
            {emailError && (
              <p id="email-error" className="text-destructive text-sm">
                {emailError}
              </p>
            )}
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role">
              Role <span className="text-destructive">*</span>
            </Label>
            <Select
              value={role}
              onValueChange={(value) =>
                setRole(value as 'viewer' | 'editor' | 'owner')
              }
              disabled={isSubmitting}
            >
              <SelectTrigger id="role" className="w-full">
                <SelectValue placeholder="Select a role">
                  {ROLE_OPTIONS.find((o) => o.value === role)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-muted-foreground text-xs">
                        {option.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              The role determines what the user can do in this workspace.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !email.trim()}>
            {isSubmitting ? (
              <>
                <FoldingCube size="sm" />
                Sending...
              </>
            ) : (
              'Send Invitation'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

